import React, { useMemo, useState } from "react";

import {
  RideIcMaleLight,
  RideButton,
  RideGlDown,
} from "@rimac-seguros/ride-system-components";
import { useSelector } from "react-redux";

import { formatAmountThousand } from "@/utils/Utils";
import helpers from "@/utils/helpers";
import { formatFullName } from "@/utils/format-name";
import useBreakpoint from "@/hooks/useBreakpoint";

import "./styles.scss";

export const CardSummary = () => {
  const isDesktop = useBreakpoint('large-min');
  const [showMore, setShowMore] = useState(!!isDesktop);
  const { nameFrequency, typeOfFinancing, planSelected } = useSelector(
    (state) => state.plans
  );
  const { userData } = useSelector((state) => state.vida);
  const { beneficiaryType, beneficiaries } = useSelector(
    (state) => state.beneficiaries
  );

  const { devolucionTotal, pagoMensual, aporteTotal, gananciaTotal } =
    typeOfFinancing || {};
  const { sumAssured, savingTime, returnPercentage } = planSelected || {};

  const list = [
    {
      text: `En ${savingTime?.text} años recibirás`,
      value: `S/${formatAmountThousand(
        Number(devolucionTotal || 0).toFixed(2)
      )}`,
      isBold: true,
    },
    {
      text: `Pago ${nameFrequency.toLowerCase()}`,
      value: `S/${formatAmountThousand(Number(pagoMensual || 0).toFixed(2))}`,
    },
    {
      text: "Ganancia extra",
      additionalText:
        returnPercentage?.text === "100%"
          ? "(Eligiendo desde un 115% de devolución)"
          : "",
      value: `S/${formatAmountThousand(Number(gananciaTotal || 0).toFixed(2))}`,
    },
    {
      text: "Aporte total",
      value: `S/${formatAmountThousand(Number(aporteTotal || 0).toFixed(2))}`,
    },
    {
      text: "Cobertura por fallecimiento",
      value: sumAssured?.text,
    },
    {
      text: "Tiempo de ahorro",
      value: `${savingTime?.text} años`,
    },
    {
      text: "Porcentaje de devolución",
      value: returnPercentage?.text,
    },
    {
      text: "Frecuencia de pago",
      value: nameFrequency,
    },
  ];

  const getFullName = useMemo(() => {
    const { nombres, apellidoPaterno } = userData;
    return `${String(nombres).toLocaleLowerCase()} ${String(
      apellidoPaterno
    ).toLocaleLowerCase()}`;
  }, [userData]);

  const callAnalyticsAction = () => {
    if (!showMore) {
      helpers.getTagAction(
        "Seguro Vida Devolución Garantizada",
        "Resumen y pago",
        "click",
        "Ver resumen",
        "trackAction"
      );
    }
    setShowMore(!showMore);
  };

  const getBeneficiaries = useMemo(() => {
    return beneficiaryType === "beneficiary-legal" ? (
      <p className="ride-sys-text-paragraph-medium-regular-short lg:ride-sys-text-paragraph-medium-regular">
        Herederos legales
      </p>
    ) : (
      <ul className="flex flex-col gap-ride-gl-spacing-large">
        {beneficiaries.map((_) => (
          <li className="flex justify-between ride-sys-text-paragraph-medium-regular-short lg:ride-sys-text-paragraph-medium-regular">
            <span>{formatFullName(_.names, _.lastName, _.surName)}</span>
            <span>{_.percentage}%</span>
          </li>
        ))}
      </ul>
    );
  }, [beneficiaryType]);

  return (
    <div className="w-full border rounded-ride-sys-border-radius-container-large border-ride-sys-color-border-default lg:sticky lg:top-24">
      <div className="pb-0 p-ride-gl-spacing-large">
        <div
          className="flex items-center justify-between py-ride-gl-spacing-2xlarge px-ride-gl-spacing-xlarge rounded-ride-sys-border-radius-container-medium lg:py-ride-gl-spacing-3xlarge lg:px-ride-gl-spacing-2xlarge"
          style={{ backgroundColor: "#EFE9FF" }}
        >
          <div className="flex flex-col gap-ride-gl-spacing-medium lg:gap-0">
            <h2 className="capitalize ride-sys-text-title-large-default lg:ride-sys-text-title-2xlarge-default">
              {getFullName}
            </h2>
            <p className="m-0 no-underline ride-sys-text-paragraph-small-default lg:ride-sys-text-paragraph-medium-regular">
              {userData.documento}
              <span className="inline no-underline">
                <small>&nbsp;•&nbsp;</small>
                {userData.email}
              </span>
            </p>
          </div>
          <RideIcMaleLight size="medium" className="hidden lg:block" />
        </div>
      </div>
      <div
        className={`flex flex-col gap-ride-gl-spacing-large py-ride-gl-spacing-2xlarge px-ride-gl-spacing-3xlarge ${
          showMore ? "expand" : "collapse"
        }`}
      >
        {list.slice(0, showMore ? list.length : 1).map((item) => (
          <div
            key={item.text}
            className={`flex justify-between items-center ${
              item.isBold
                ? "ride-sys-text-paragraph-medium-bold-short lg:ride-sys-text-paragraph-large-bold"
                : "ride-sys-text-paragraph-medium-regular-short lg:ride-sys-text-paragraph-medium-regular"
            }`}
          >
            <span>
              {item.text}
              {item.additionalText && (
                <span className="hidden ride-sys-text-paragraph-small-default text-ride-sys-color-text-medium lg:block">
                  {item.additionalText}
                </span>
              )}
            </span>
            <span
              className={`capitalize ${
                item.isBold ? "lg:ride-sys-text-title-2xlarge-default" : ""
              }`}
            >
              {item.value}
            </span>
          </div>
        ))}

        {beneficiaryType && showMore && (
          <div className="border-t mt-ride-gl-spacing-large pt-ride-gl-spacing-2xlarge pb-ride-gl-spacing-large border-ride-sys-color-border-default">
            <div className="ride-sys-text-label-small-default mb-ride-gl-spacing-large lg:ride-sys-text-paragraph-small-bold">
              Beneficiarios{" "}
              {!!beneficiaries.length && <span>({beneficiaries.length})</span>}
            </div>
            {getBeneficiaries}
          </div>
        )}
      </div>
      <div className="border-t border-ride-sys-color-border-default">
        <RideButton
          text={!showMore ? "Ver resumen" : "Ocultar resumen"}
          variant="text-primary"
          size="medium"
          glyph={
            <RideGlDown
              className={
                showMore ? "transform rotate-180 transition-all delay-200" : ""
              }
            />
          }
          glyphPosition="right"
          className="w-full"
          onClick={callAnalyticsAction}
        />
      </div>
    </div>
  );
};
